import { useState, useEffect, useCallback } from "react";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { FileText, DollarSign, Clock, CheckCircle2, AlertCircle, Eye, CreditCard, Building2, Upload, Copy, AlertTriangle, Loader2, Landmark, Hash } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  paid: "bg-emerald-100 text-emerald-700",
  overdue: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-400",
};

const confirmStatusColors = {
  pending_review: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};

function CopyBtn({ text, label }) {
  return (
    <button type="button" className="p-0.5 hover:bg-slate-200 rounded ml-1 inline-flex" onClick={() => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); }}>
      <Copy className="h-3 w-3 text-slate-400" />
    </button>
  );
}

function BankDetailsSection({ compact = false }) {

  return (
    <div className={compact ? "space-y-3" : "space-y-4"} data-testid="bank-details-section">
      {/* Bahamas */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="bank-bahamas">
        <div className="flex items-center gap-2 mb-3">
          <Landmark className="h-4 w-4 text-blue-700" />
          <p className="font-semibold text-blue-800 text-sm">Bahamas Clients — Scotiabank</p>
        </div>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-blue-600">Bank</span><span className="font-semibold text-blue-900">Scotiabank Bahamas</span></div>
          <div className="flex justify-between"><span className="text-blue-600">Account Name</span><span className="font-semibold text-blue-900">Sebastian Alliance Group Ltd.</span></div>
          <div className="flex justify-between"><span className="text-blue-600">Account #</span><span className="font-mono font-semibold text-blue-900">171287<CopyBtn text="171287" label="Account number" /></span></div>
          <div className="flex justify-between"><span className="text-blue-600">Branch</span><span className="font-mono font-semibold text-blue-900">03465 (Carmichael)<CopyBtn text="03465" label="Branch number" /></span></div>
        </div>
      </div>
      {/* US */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="bank-us">
        <div className="flex items-center gap-2 mb-3">
          <Landmark className="h-4 w-4 text-emerald-700" />
          <p className="font-semibold text-emerald-800 text-sm">U.S. Clients — Wells Fargo</p>
        </div>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-emerald-600">Bank</span><span className="font-semibold text-emerald-900">Wells Fargo</span></div>
          <div className="flex justify-between"><span className="text-emerald-600">Account Name</span><span className="font-semibold text-emerald-900">Sebastian Alliance Group LLC</span></div>
          <div className="flex justify-between"><span className="text-emerald-600">Account #</span><span className="font-mono font-semibold text-emerald-900">8387238879<CopyBtn text="8387238879" label="Account number" /></span></div>
          <div className="flex justify-between"><span className="text-emerald-600">Routing #</span><span className="font-mono font-semibold text-emerald-900">063107513<CopyBtn text="063107513" label="Routing number" /></span></div>
        </div>
      </div>
    </div>
  );
}

export default function ClientInvoices() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailInvoice, setDetailInvoice] = useState(null);

  // Deposit confirmation / reference submission state
  const [uploadInvoice, setUploadInvoice] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [refNumber, setRefNumber] = useState("");
  const [depositDate, setDepositDate] = useState("");
  const [uploadNotes, setUploadNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [confirmations, setConfirmations] = useState({});

  // Payment processor selection dialog
  const [payDialog, setPayDialog] = useState(null); // { invoice }
  const [paying, setPaying] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await apiClient.get("/invoices");
      setInvoices(res.data);
    } catch { toast.error("Failed to load invoices"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // Fetch deposit confirmations for pending invoices
  useEffect(() => {
    const fetchConfirmations = async () => {
      const pending = invoices.filter(i => i.status === "pending" || i.status === "overdue");
      for (const inv of pending) {
        try {
          const res = await apiClient.get(`/invoices/${inv.id}/deposit-confirmation`);
          if (res.data.length > 0) {
            setConfirmations(prev => ({ ...prev, [inv.id]: res.data[0] }));
          }
        } catch {}
      }
    };
    if (invoices.length > 0) fetchConfirmations();
  }, [invoices]);

  const handleSubmitReference = async () => {
    if (!refNumber.trim() || !depositDate) {
      toast.error("Please enter a reference number and deposit date");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("reference_number", refNumber.trim());
      formData.append("deposit_date", depositDate);
      formData.append("notes", uploadNotes.trim());
      if (uploadFile) {
        formData.append("file", uploadFile);
      }

      const res = await apiClient.post(
        `/invoices/${uploadInvoice.id}/deposit-confirmation`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      toast.success(res.data.message || "Payment reference submitted!");
      setUploadInvoice(null);
      setUploadFile(null);
      setRefNumber("");
      setDepositDate("");
      setUploadNotes("");
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Submission failed");
    } finally {
      setUploading(false);
    }
  };

  const openPayDialog = (inv) => {
    // Processor is configured by SAG on the invoice/SLA contract and is not payer-selectable.
    setPayDialog({ invoice: inv });
  };

  const handleConfirmPayment = async () => {
    if (!payDialog?.invoice) return;
    setPaying(true);
    try {
      // Map portal language (en/es/fr) → Fiserv codes on the backend
      const res = await apiClient.post(`/invoices/${payDialog.invoice.id}/pay`, {
        payment_method: "card",
        language: language || "en",
        origin_url: window.location.origin,
      });

      if (res.data.processor === "fiserv" && res.data.gateway_url && res.data.form_params) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = res.data.gateway_url;
        Object.entries(res.data.form_params).forEach(([key, value]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment initiation failed");
      setPaying(false);
    }
  };

  const handleDownloadReceipt = (inv) => {
    // Open receipt in new tab (uses cookie-less auth via Bearer token in localStorage → but browser
    // request won't send Authorization header. We fetch via apiClient and open blob URL instead.)
    apiClient
      .get(`/invoices/${inv.id}/receipt`, { responseType: "text" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      })
      .catch((err) => toast.error(err.response?.data?.detail || "Failed to load receipt"));
  };

  const outstanding = invoices.filter((i) => i.status === "pending" || i.status === "overdue");
  const totalOutstanding = outstanding.reduce((sum, i) => sum + i.total, 0);

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-sag-navy border-t-transparent rounded-full" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-invoices">
        <h1 className="font-heading text-2xl font-bold text-slate-900">{t("nav.myInvoices") || "My Invoices"}</h1>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="sag-card"><CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-sag-navy/10 text-sag-navy"><FileText className="h-5 w-5" /></div>
            <div><p className="text-xl font-bold font-heading">{invoices.length}</p><p className="text-xs text-slate-500">Total Invoices</p></div>
          </CardContent></Card>
          <Card className="sag-card"><CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-100 text-amber-600"><Clock className="h-5 w-5" /></div>
            <div><p className="text-xl font-bold font-heading">${totalOutstanding.toLocaleString()}</p><p className="text-xs text-slate-500">Outstanding</p></div>
          </CardContent></Card>
          <Card className="sag-card"><CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-600"><CheckCircle2 className="h-5 w-5" /></div>
            <div><p className="text-xl font-bold font-heading">{invoices.filter((i) => i.status === "paid").length}</p><p className="text-xs text-slate-500">Paid</p></div>
          </CardContent></Card>
        </div>

        {/* Outstanding Alert */}
        {outstanding.length > 0 && (
          <Card className="border-amber-200 bg-amber-50" data-testid="outstanding-invoices-alert">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <p className="text-sm text-amber-800">You have <strong>{outstanding.length}</strong> outstanding invoice(s) totalling <strong>${totalOutstanding.toLocaleString()}</strong>. Please arrange payment at your earliest convenience.</p>
            </CardContent>
          </Card>
        )}

        {/* Invoice List */}
        <Card className="sag-card">
          <CardHeader><CardTitle className="font-heading text-lg">Invoice History</CardTitle></CardHeader>
          <CardContent className="p-0">
            {invoices.length === 0 ? (
              <div className="p-12 text-center text-slate-500"><FileText className="h-10 w-10 mx-auto mb-2 text-slate-300" /><p>No invoices yet</p></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => {
                    const conf = confirmations[inv.id];
                    return (
                      <TableRow key={inv.id} data-testid={`client-invoice-${inv.id}`}>
                        <TableCell className="font-mono text-sm font-medium">{inv.invoice_number}</TableCell>
                        <TableCell>
                          <p className="text-sm">{inv.items?.[0]?.description?.split("(")[0]?.trim() || inv.billing_type}</p>
                          <p className="text-xs text-slate-500 capitalize">{inv.billing_type}</p>
                        </TableCell>
                        <TableCell className="font-semibold">${inv.total.toLocaleString()} {inv.currency}</TableCell>
                        <TableCell className="text-sm">{new Date(inv.due_date + "T00:00:00").toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[inv.status]}><span className="capitalize">{inv.status}</span></Badge>
                          {conf && (
                            <Badge className={`ml-1 ${confirmStatusColors[conf.status]}`}>
                              <span className="capitalize text-[10px]">{conf.status === "pending_review" ? "Ref Submitted" : conf.status}</span>
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setDetailInvoice(inv)} data-testid={`view-client-invoice-${inv.id}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            {(inv.status === "pending" || inv.status === "overdue") && inv.payment_method !== "bank_transfer" && (
                              <Button
                                size="sm"
                                className="text-xs bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => openPayDialog(inv)}
                                data-testid={`pay-now-${inv.id}`}
                              >
                                <CreditCard className="h-3 w-3 mr-1" /> Pay Now
                              </Button>
                            )}
                            {(inv.status === "pending" || inv.status === "overdue") && (!conf || conf.status === "rejected") && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={() => setUploadInvoice(inv)}
                                data-testid={`confirm-payment-${inv.id}`}
                              >
                                <Hash className="h-3 w-3 mr-1" /> {conf?.status === "rejected" ? "Re-submit Reference" : "Submit Payment Ref"}
                              </Button>
                            )}
                            {inv.status === "paid" && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={() => handleDownloadReceipt(inv)}
                                data-testid={`download-receipt-${inv.id}`}
                              >
                                <FileText className="h-3 w-3 mr-1" /> Receipt
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Enhanced Invoice Detail Dialog */}
        <Dialog open={!!detailInvoice} onOpenChange={() => setDetailInvoice(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Invoice Detail</DialogTitle></DialogHeader>
            {detailInvoice && (
              <div className="space-y-5">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-lg font-bold" data-testid="detail-invoice-number">{detailInvoice.invoice_number}</p>
                    <p className="text-sm text-slate-500">{detailInvoice.client_name}</p>
                  </div>
                  <Badge className={`text-sm px-3 py-1 ${statusColors[detailInvoice.status]}`}><span className="capitalize">{detailInvoice.status}</span></Badge>
                </div>

                {/* Key Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3" data-testid="detail-amount">
                    <p className="text-xs text-slate-400 uppercase">Amount Due</p>
                    <p className="font-bold text-xl text-sag-navy">${detailInvoice.total.toLocaleString()} {detailInvoice.currency}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3" data-testid="detail-due-date">
                    <p className="text-xs text-slate-400 uppercase">Due Date</p>
                    <p className="font-semibold">{new Date(detailInvoice.due_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3" data-testid="detail-billing-type">
                    <p className="text-xs text-slate-400 uppercase">Billing Type</p>
                    <p className="font-semibold capitalize">{detailInvoice.billing_type}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3" data-testid="detail-payment-method">
                    <p className="text-xs text-slate-400 uppercase">Payment Method</p>
                    <p className="font-semibold capitalize flex items-center gap-1">
                      {detailInvoice.payment_method === "bank_transfer" ? <Building2 className="h-3.5 w-3.5" /> : <CreditCard className="h-3.5 w-3.5" />}
                      {(detailInvoice.payment_method || "card/deposit").replace("_", " ")}
                    </p>
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Line Items</p>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    {detailInvoice.items.map((item, i) => (
                      <div key={`item-${item.description}-${i}`} className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0">
                        <div><p className="text-sm font-medium">{item.description}</p><p className="text-xs text-slate-500">{item.quantity} x ${item.unit_price.toLocaleString()}</p></div>
                        <p className="font-semibold">${item.total.toLocaleString()}</p>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-3 bg-slate-50 font-bold">
                      <span>Total Due</span>
                      <span className="text-sag-navy text-lg">${detailInvoice.total.toLocaleString()} {detailInvoice.currency}</span>
                    </div>
                  </div>
                </div>

                {detailInvoice.notes && <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{detailInvoice.notes}</p>}

                {/* Payment Confirmed */}
                {detailInvoice.manual_payment && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                    <p className="font-medium text-emerald-700 mb-1">Payment Confirmed</p>
                    <p>Ref: {detailInvoice.manual_payment.reference_number} | Bank: {detailInvoice.manual_payment.bank_name}</p>
                    <p>Date: {detailInvoice.manual_payment.date_received}</p>
                  </div>
                )}

                {/* Deposit Confirmation Status */}
                {confirmations[detailInvoice.id] && (
                  <div className={`rounded-lg p-3 text-sm border ${
                    confirmations[detailInvoice.id].status === "approved" ? "bg-emerald-50 border-emerald-200" :
                    confirmations[detailInvoice.id].status === "rejected" ? "bg-red-50 border-red-200" :
                    "bg-amber-50 border-amber-200"
                  }`}>
                    <p className="font-medium mb-1">
                      {confirmations[detailInvoice.id].status === "pending_review" && "Payment reference submitted — awaiting admin review"}
                      {confirmations[detailInvoice.id].status === "approved" && "Payment reference approved"}
                      {confirmations[detailInvoice.id].status === "rejected" && "Payment reference rejected — please re-submit"}
                    </p>
                    <p className="text-xs">Ref: {confirmations[detailInvoice.id].reference_number} | Date: {confirmations[detailInvoice.id].deposit_date}</p>
                    {confirmations[detailInvoice.id].file_name && <p className="text-xs">File: {confirmations[detailInvoice.id].file_name}</p>}
                    {confirmations[detailInvoice.id].review_notes && (
                      <p className="text-xs mt-1">Admin notes: {confirmations[detailInvoice.id].review_notes}</p>
                    )}
                  </div>
                )}

                {/* Direct Deposit Instructions */}
                {(detailInvoice.status === "pending" || detailInvoice.status === "overdue") && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase mb-3">Direct Deposit Payment Instructions</p>
                      <BankDetailsSection />
                    </div>
                  </>
                )}

                {/* Action Buttons */}
                {(detailInvoice.status === "pending" || detailInvoice.status === "overdue") && (
                  <div className="space-y-2 pt-2">
                    {detailInvoice.payment_method !== "bank_transfer" && (
                      <Button
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => { setDetailInvoice(null); openPayDialog(detailInvoice); }}
                        data-testid="pay-now-from-detail"
                      >
                        <CreditCard className="h-4 w-4 mr-2" /> Pay Now with Credit Card
                      </Button>
                    )}
                    {(!confirmations[detailInvoice.id] || confirmations[detailInvoice.id]?.status === "rejected") && (
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => { setDetailInvoice(null); setUploadInvoice(detailInvoice); }}
                        data-testid="confirm-payment-from-detail"
                      >
                        <Hash className="h-4 w-4 mr-2" /> Submit Deposit Reference Number
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Submit Payment Reference Dialog */}
        <Dialog open={!!uploadInvoice} onOpenChange={(open) => { if (!open) { setUploadInvoice(null); setUploadFile(null); setRefNumber(""); setDepositDate(""); setUploadNotes(""); } }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Hash className="h-5 w-5 text-emerald-600" />
                Submit Payment Reference
              </DialogTitle>
            </DialogHeader>
            {uploadInvoice && (
              <div className="space-y-4">
                {/* Invoice Reference */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Invoice</span>
                    <span className="font-mono font-semibold">{uploadInvoice.invoice_number}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-500">Amount Due</span>
                    <span className="font-bold text-sag-navy">${uploadInvoice.total.toLocaleString()} {uploadInvoice.currency}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-500">Due Date</span>
                    <span className="font-medium">{new Date(uploadInvoice.due_date + "T00:00:00").toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Dual Bank Details */}
                <BankDetailsSection compact />

                {/* Reference Submission Form */}
                <Separator />
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="refNumber" className="text-sm font-medium">Deposit Reference / Confirmation Number *</Label>
                    <Input
                      id="refNumber"
                      value={refNumber}
                      onChange={(e) => setRefNumber(e.target.value)}
                      placeholder="e.g., TXN-123456 or bank reference number"
                      className="mt-1"
                      data-testid="deposit-ref-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="depositDate" className="text-sm font-medium">Date of Deposit *</Label>
                    <Input
                      id="depositDate"
                      type="date"
                      value={depositDate}
                      onChange={(e) => setDepositDate(e.target.value)}
                      className="mt-1"
                      data-testid="deposit-date-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="depositFile" className="text-sm font-medium">Deposit Receipt / Screenshot (optional)</Label>
                    <Input
                      id="depositFile"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,application/pdf"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      className="mt-1"
                      data-testid="deposit-file-input"
                    />
                    <p className="text-xs text-slate-400 mt-0.5">PNG, JPEG, WebP, or PDF (max 10MB)</p>
                  </div>

                  <div>
                    <Label htmlFor="uploadNotes" className="text-sm font-medium">Additional Notes</Label>
                    <Textarea
                      id="uploadNotes"
                      value={uploadNotes}
                      onChange={(e) => setUploadNotes(e.target.value)}
                      placeholder="Any additional details about the deposit..."
                      className="mt-1"
                      rows={2}
                      data-testid="deposit-notes-input"
                    />
                  </div>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    After submission, our team will verify your payment reference and activate your services promptly.
                    Please ensure the deposit amount matches the invoice total.
                  </p>
                </div>

                <Button
                  onClick={handleSubmitReference}
                  disabled={uploading || !refNumber.trim() || !depositDate}
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-semibold"
                  data-testid="submit-deposit-confirmation"
                >
                  {uploading ? (
                    <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</span>
                  ) : (
                    <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Submit Payment Reference</span>
                  )}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Processor Selection Dialog */}
        <Dialog open={!!payDialog} onOpenChange={() => !paying && setPayDialog(null)}>
          <DialogContent className="max-w-md" data-testid="payment-processor-dialog">
            <DialogHeader>
              <DialogTitle className="text-lg font-heading">Choose Payment Method</DialogTitle>
            </DialogHeader>
            {payDialog?.invoice && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex justify-between text-sm text-slate-600 mb-1">
                    <span>{payDialog.invoice.invoice_number}</span>
                    <span>{payDialog.invoice.currency || "USD"}</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    ${(payDialog.invoice.total || 0).toFixed(2)}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-slate-700">Configured Payment Processor</Label>
                  <div className="w-full p-4 rounded-lg border-2 border-blue-200 bg-blue-50" data-testid="configured-payment-processor">
                    {payDialog.invoice.payment_processor === "fiserv" ? (
                      <div>
                        <div className="font-semibold text-slate-900 flex items-center gap-2">
                          <Landmark className="h-4 w-4 text-emerald-700" /> Fiserv IPG (Scotiabank)
                          <Badge className="bg-slate-100 text-slate-700 text-[10px]">Configured by SAG</Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">Bahamas card payment in BSD via the configured merchant gateway.</div>
                      </div>
                    ) : (
                      <div>
                        <div className="font-semibold text-slate-900 flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-blue-600" /> Stripe
                          <Badge className="bg-blue-100 text-blue-700 text-[10px]">Configured by SAG</Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">Secure card payment using the processor configured for this invoice.</div>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">For security and reconciliation, the payment processor is locked to the invoice/SLA configuration.</p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  💡 The payment page will open in <strong>{language === "es" ? "Spanish (Español)" : language === "fr" ? "French (Français)" : "English"}</strong> based on your portal preference.
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setPayDialog(null)}
                    disabled={paying}
                    data-testid="cancel-payment-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={handleConfirmPayment}
                    disabled={paying}
                    data-testid="confirm-payment-btn"
                  >
                    {paying ? (
                      <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Redirecting...</span>
                    ) : (
                      <span className="flex items-center gap-2"><CreditCard className="h-4 w-4" /> Continue to Payment</span>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
